import React from 'react';
import { hardRefresh } from '../utils/versionCheck';

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorMessage: error.message || 'An unexpected error occurred'
    };
  }

  componentDidCatch(error) {
    const isChunkError =
      /loading chunk/i.test(error.message) ||
      /failed to fetch dynamically imported/i.test(error.message) ||
      /loading css chunk/i.test(error.message) ||
      /Unexpected token '<'/i.test(error.message) ||
      error.name === 'ChunkLoadError' ||
      error.name === 'SyntaxError';

    if (isChunkError) {
      const lastReload = sessionStorage.getItem('chunk_reload_time');
      const now = Date.now();
      const isRecentReload = lastReload && (now - parseInt(lastReload, 10) < 10000);
      
      if (!isRecentReload) {
        sessionStorage.setItem('chunk_reload_time', now.toString());
        this.cacheBustReload();
        return;
      }
    }

    console.error('ChunkErrorBoundary caught error:', error);
  }

  // Plain reload() can still be served from browser/CDN HTTP cache, which is
  // how a stale build kept reproducing the same missing-chunk error after
  // "refreshing". Navigating to a URL with a unique query param forces a
  // real network request instead of a cache hit, while keeping the user on
  // the same route and leaving their session storage intact.
  // Note: 'chunk_reload' is intentionally NOT cleared here - clearing it would
  // erase the one-time auto-retry guard that componentDidCatch just set,
  // right before the guarded reload even lands, letting a persistent (not
  // just stale-cache) chunk failure retrigger this on every reload forever.
  cacheBustReload = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('_cr', Date.now());
    window.location.href = url.toString();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'Poppins, sans-serif',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#f5f5f5'
        }}>
          <h2 style={{ marginBottom: '16px', color: '#333' }}>
            Oops! Something went wrong
          </h2>
          <p style={{ marginBottom: '24px', color: '#666', fontSize: '14px' }}>
            The app encountered an unexpected error. This may be due to outdated cache or a deployment issue.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={this.cacheBustReload}
              style={{
                padding: '12px 32px',
                fontSize: '16px',
                backgroundColor: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Soft Refresh
            </button>
            <button
              onClick={hardRefresh}
              style={{
                padding: '12px 32px',
                fontSize: '16px',
                backgroundColor: '#d32f2f',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Hard Refresh (Clear Cache)
            </button>
          </div>
          <p style={{
            marginTop: '32px',
            color: '#999',
            fontSize: '12px',
            fontFamily: 'monospace'
          }}>
            Error: {this.state.errorMessage}
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
